/**
 * Emails List Page
 * Handles select-all checkbox, bulk actions, and drag-and-drop to sidebar folders.
 *
 * Phase 21.B (2026-05-11) — module is now the single owner of the email
 * sandboxed-iframe rendering and the print-to-iframe helper. The previous
 * 3 inline ``<script>`` blocks (inbox.html / reply.html / read.html) and
 * the 4 in-module duplications of the iframe ``srcdoc`` wrapper were
 * collapsed into ``renderEmailBodyFrame()`` + ``printEmailFrame()`` +
 * ``window.printEmail`` (driven by the ``data-print-email`` declarative).
 */

import { lockButton, unlockButton } from '../utils/forms/submit-button.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

// ═══════════════════════════════════════════════════════
//  Email body iframe + print helpers (shared infra)
// ═══════════════════════════════════════════════════════

// HTML <head> + <style> wrapper used to render quoted email bodies inside
// a sandboxed iframe (CSS isolation + ``<base target="_blank">`` so links
// open in a new tab instead of inside the sandbox). Email HTML is wildly
// inconsistent — keeping this wrapper in ONE place means a future tweak
// (font, link colour, table overflow) lands everywhere at once.
const EMAIL_BODY_FRAME_WRAPPER_HEAD =
  '<!DOCTYPE html><html><head><meta charset="utf-8">' +
  '<meta name="viewport" content="width=device-width,initial-scale=1">' +
  '<base target="_blank">' +
  '<style>body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#374151;font-size:14px;line-height:1.5}' +
  'a{color:#4971a8}img{max-width:100%;height:auto}table{max-width:100%}</style>' +
  '</head><body>';

// CSS for the print iframe (the user-visible printout). Distinct from
// EMAIL_BODY_FRAME_WRAPPER_HEAD because the print layout has a header
// block (subject + meta lines) that the in-page preview doesn't.
const EMAIL_PRINT_STYLE =
  'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;margin:40px;color:#111;line-height:1.5}' +
  'h1{font-size:18px;margin:0 0 16px}' +
  '.meta{font-size:13px;color:#555;margin-bottom:4px}' +
  '.meta strong{color:#111}' +
  'hr{border:none;border-top:1px solid #ddd;margin:16px 0}' +
  '.body{font-size:14px}' +
  '.body img{max-width:100%}';

// Defaults are English so the helper is safe to call before any
// init-time config. Each ``initEmail*`` call below overwrites this with
// the localized strings supplied by the template via data-* attributes.
const _printI18n = {
  fromText: 'From',
  toText: 'To',
  dateText: 'Date',
};

/**
 * Override the labels used inside the print iframe ("From" / "To" /
 * "Date"). Called once per init-time from each ``initEmail*`` entry
 * point — last-write-wins is fine because every page on a single
 * SPA navigation is in the same locale.
 */
export function setEmailPrintI18n(i18n = {}) {
  if (i18n.fromText) _printI18n.fromText = i18n.fromText;
  if (i18n.toText) _printI18n.toText = i18n.toText;
  if (i18n.dateText) _printI18n.dateText = i18n.dateText;
}

/**
 * Render every ``.email-body-data`` template inside ``rootEl`` into the
 * matching ``.email-body-frame`` sibling iframe via ``srcdoc`` and bind
 * an autoresize ``load`` listener that sets the iframe height to its
 * scrollHeight (with a 500ms re-measure to catch lazy-loaded images).
 *
 * Idempotent at the iframe level via the ``_emailBodyFrameRendered``
 * sentinel — repeated calls (e.g. after HTMX afterSwap) are safe.
 *
 * @param {Element|Document} [rootEl=document]
 */
export function renderEmailBodyFrame(rootEl) {
  const scope = rootEl && rootEl.querySelectorAll ? rootEl : document;
  const frames = scope.querySelectorAll('.email-body-frame');
  frames.forEach((frame) => {
    if (frame._emailBodyFrameRendered) return;
    // Find the matching template — first try as a sibling/descendant of
    // the same parent block, then fall back to a global lookup. Both
    // inbox preview and reply page emit the template right next to the
    // iframe, so the local search succeeds in practice.
    const parent = frame.parentElement || scope;
    const tpl = parent.querySelector('.email-body-data')
      || (scope.querySelector ? scope.querySelector('.email-body-data') : null);
    if (!tpl) return;
    frame._emailBodyFrameRendered = true;
    frame.srcdoc = EMAIL_BODY_FRAME_WRAPPER_HEAD + tpl.innerHTML + '</body></html>';
    frame.addEventListener('load', function () {
      try {
        const h = frame.contentDocument.documentElement.scrollHeight;
        frame.style.height = h + 'px';
        // Re-measure once images / late layout settle.
        setTimeout(function () {
          try {
            frame.style.height = frame.contentDocument.documentElement.scrollHeight + 'px';
          } catch (e) { /* cross-origin guard */ }
        }, 500);
      } catch (e) { /* cross-origin guard */ }
    });
  });
}

/**
 * Open a hidden print-iframe with a styled email "letterhead" (subject +
 * From / To / Date meta) followed by the email body, then trigger the
 * browser's print dialog. The iframe is removed 1s after the dialog is
 * dismissed (printing happens synchronously inside ``contentWindow.print``
 * on every modern browser, so 1s is comfortably safe).
 *
 * @param {object} meta
 * @param {string} meta.subject  - Email subject (used as <title> + <h1>).
 * @param {string} meta.from     - "From" header value.
 * @param {string} [meta.to]     - "To" header value (omitted in the
 *                                 letterhead if empty — saves a row when
 *                                 the email is from-only e.g. notifications).
 * @param {string} meta.date     - Pre-formatted human date string.
 * @param {string} meta.bodyHtml - The body HTML to embed (already trusted —
 *                                 same-origin iframe, the user's own data).
 */
export function printEmailFrame({ subject = '', from = '', to = '', date = '', bodyHtml = '' } = {}) {
  const printFrame = document.createElement('iframe');
  printFrame.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:800px;height:600px;border:none;';
  document.body.appendChild(printFrame);

  const doc = printFrame.contentDocument || printFrame.contentWindow.document;
  doc.open();
  doc.write(
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    '<title>' + subject + '</title>' +
    '<style>' + EMAIL_PRINT_STYLE + '</style></head><body>' +
    '<h1>' + subject + '</h1>' +
    '<div class="meta"><strong>' + _printI18n.fromText + ':</strong> ' + from + '</div>' +
    (to ? '<div class="meta"><strong>' + _printI18n.toText + ':</strong> ' + to + '</div>' : '') +
    '<div class="meta"><strong>' + _printI18n.dateText + ':</strong> ' + date + '</div>' +
    '<hr>' +
    '<div class="body">' + bodyHtml + '</div>' +
    '</body></html>'
  );
  doc.close();

  printFrame.contentWindow.focus();
  printFrame.contentWindow.print();
  setTimeout(function () {
    if (printFrame.parentNode) printFrame.parentNode.removeChild(printFrame);
  }, 1000);
}

// Locate the body HTML to print. Prefers the (only) ``.email-body-frame``
// iframe present on the page (works for inbox preview AND reply page —
// both classes were unified in Phase 21.B). Falls back to the plain-text
// snippet rendered as a paragraph in the inbox preview when an email has
// no HTML body. Returns '' when neither is available — printEmailFrame
// then prints just the meta letterhead with an empty body block.
function _readEmailBodyForPrint() {
  const iframe = document.querySelector('.email-body-frame');
  if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
    return iframe.contentDocument.body.innerHTML;
  }
  const previewPane = document.getElementById('email-preview-content');
  if (previewPane) {
    const plainText = previewPane.querySelector('article p');
    if (plainText) {
      return '<pre style="white-space:pre-wrap;font-family:sans-serif">' + plainText.textContent + '</pre>';
    }
  }
  return '';
}

// Window-scoped because ``data-print-email`` (in
// utils/declarative-attrs.js) calls ``window.printEmail(trigger)`` —
// keeping it global keeps the declarative attribute self-contained
// without requiring every consumer template to know about ESM imports.
window.printEmail = function printEmail(btn) {
  if (!btn || !btn.getAttribute) return;
  printEmailFrame({
    subject: btn.getAttribute('data-print-subject') || '',
    from: btn.getAttribute('data-print-from') || '',
    to: btn.getAttribute('data-print-to') || '',
    date: btn.getAttribute('data-print-date') || '',
    bodyHtml: _readEmailBodyForPrint(),
  });
};

/**
 * Submit a bulk action form for the given message IDs.
 * @param {string} actionUrl   - The URL to POST to.
 * @param {string} csrfToken   - Django CSRF token.
 * @param {string[]} messageIds - Array of message IDs to include.
 * @param {object} extraFields  - Optional extra hidden fields (e.g. { folder: 'inbox' }).
 */
function submitBulkActionForIds(actionUrl, csrfToken, messageIds, extraFields = {}) {
  if (!actionUrl || messageIds.length === 0) return;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionUrl;
  form.style.display = 'none';

  // CSRF token
  const csrf = document.createElement('input');
  csrf.type = 'hidden';
  csrf.name = 'csrfmiddlewaretoken';
  csrf.value = csrfToken;
  form.appendChild(csrf);

  // Message IDs
  messageIds.forEach((id) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'message_ids';
    input.value = id;
    form.appendChild(input);
  });

  // Extra fields
  Object.entries(extraFields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

/**
 * Submit a bulk action form for the currently checked messages.
 * @param {string} actionUrl  - The URL to POST to.
 * @param {string} csrfToken  - Django CSRF token.
 * @param {object} extraFields - Optional extra hidden fields to include.
 */
function submitBulkAction(actionUrl, csrfToken, extraFields = {}) {
  const checked = document.querySelectorAll('.mail-row-checkbox:checked');
  if (checked.length === 0) return;
  const ids = Array.from(checked).map((cb) => cb.getAttribute('data-message-id'));
  submitBulkActionForIds(actionUrl, csrfToken, ids, extraFields);
}

/**
 * Initialize the email list page (split-view + bulk actions).
 * @param {object} options - Configuration passed from the Django template.
 * @param {string} options.folder               - Current folder: 'inbox', 'sent', 'trash', 'archive'.
 * @param {string} options.csrfToken            - Django CSRF token for POST forms.
 * @param {string} options.bulkTrashUrl         - URL to POST selected message IDs for trash.
 * @param {string} options.bulkArchiveUrl       - URL to POST selected message IDs for archive.
 * @param {string} options.bulkMarkUnreadUrl    - URL to POST selected message IDs for mark-as-unread.
 * @param {string} options.bulkMoveToInboxUrl   - URL to POST selected message IDs to move to inbox.
 * @param {string} options.toggleStarUrlTemplate - URL template for toggle-star (contains __MSG_ID__).
 * @param {string} options.readUrlTemplate       - URL template for full-page read (contains __MSG_ID__).
 * @param {string} options.previewUrlTemplate    - URL template for AJAX preview (contains __MSG_ID__).
 */
export function initEmailList(options = {}) {
  const {
    folder = 'inbox',
    csrfToken = '',
    bulkTrashUrl = '',
    bulkArchiveUrl = '',
    bulkMarkUnreadUrl = '',
    bulkMoveToInboxUrl = '',
    toggleStarUrlTemplate = '',
    readUrlTemplate = '',
    previewUrlTemplate = '',
    recipientRequiredText = 'Recipient required',
    invalidEmailText = 'Please make sure all addresses are properly formed.',
    errorTitle = 'Error',
    replySentText = 'Reply sent.',
    sendErrorText = 'Could not send reply. Please try again.',
    printFromText,
    printToText,
    printDateText,
  } = options;

  setEmailPrintI18n({ fromText: printFromText, toText: printToText, dateText: printDateText });

  initComposeModal({ sendErrorText });

  const checkAll = document.getElementById('checkbox-all');
  const rowCheckboxes = document.querySelectorAll('.mail-row-checkbox');
  const deleteBtn = document.getElementById('mail-delete-btn');
  const archiveBtn = document.getElementById('mail-archive-btn');
  const unreadBtn = document.getElementById('mail-unread-btn');

  if (!checkAll || rowCheckboxes.length === 0) return;

  // ── Select all / deselect all ──
  checkAll.addEventListener('change', () => {
    rowCheckboxes.forEach((cb) => {
      cb.checked = checkAll.checked;
    });
  });

  // ── Keep "select all" in sync with individual checkboxes ──
  rowCheckboxes.forEach((cb) => {
    cb.addEventListener('change', () => {
      const checkedCount = document.querySelectorAll('.mail-row-checkbox:checked').length;
      checkAll.checked = checkedCount === rowCheckboxes.length;
      checkAll.indeterminate = checkedCount > 0 && checkedCount < rowCheckboxes.length;
    });
  });

  // ── Delete icon: move selected messages to trash ──
  if (deleteBtn && bulkTrashUrl) {
    deleteBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      submitBulkAction(bulkTrashUrl, csrfToken);
    });
  }

  // ── Archive icon: move selected messages to archive ──
  if (archiveBtn && bulkArchiveUrl) {
    archiveBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      submitBulkAction(bulkArchiveUrl, csrfToken);
    });
  }

  // ── Mark as unread icon: mark selected messages as unread ──
  if (unreadBtn && bulkMarkUnreadUrl) {
    unreadBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      submitBulkAction(bulkMarkUnreadUrl, csrfToken, { folder });
    });
  }

  // ── Drag and Drop: email rows → sidebar folders ──
  initDragAndDrop({
    folder,
    csrfToken,
    bulkTrashUrl,
    bulkArchiveUrl,
    bulkMoveToInboxUrl,
  });

  // ── Star / Flag toggle ──
  if (toggleStarUrlTemplate) {
    initStarToggle({ csrfToken, toggleStarUrlTemplate });
  }

  // ── Split-view preview pane (lg+ screens) ──
  if (previewUrlTemplate) {
    initSplitViewPreview({
      folder,
      csrfToken,
      readUrlTemplate,
      previewUrlTemplate,
      toggleStarUrlTemplate,
      recipientRequiredText,
      invalidEmailText,
      errorTitle,
      replySentText,
      sendErrorText,
    });
  }

  // ── Infinite scroll ──
  if (options.loadMoreUrl) {
    initInfiniteScroll({
      folder,
      csrfToken,
      loadMoreUrl: options.loadMoreUrl,
      toggleStarUrlTemplate,
      readUrlTemplate,
      previewUrlTemplate,
      q: options.q || '',
      recipientRequiredText,
      invalidEmailText,
      errorTitle,
      replySentText,
      sendErrorText,
    });
  }
}


// ═══════════════════════════════════════════════════════
//  Compose modal — canonical dynamic shell pattern
// ═══════════════════════════════════════════════════════

/**
 * Wire the "New mail" compose modal using the canonical dynamic-modal
 * helper (see ``form-modals.mdc`` and ``frontend/src/utils/ui/dynamic-modal.js``).
 *
 * - Modal shell is static in ``inbox.html``; the form (incl. TipTap
 *   toolbar + editor) is fetched fresh from ``inbox_compose_send`` on
 *   every open via GET AJAX.
 * - On validation error, the server returns the same partial with
 *   ``status=422``; the helper re-injects it inline. TipTap is then
 *   re-initialised by ``onContentInjected`` and the previously typed
 *   body is restored from ``#wysiwyg-example[data-content]``.
 * - There is no client-side reset / cache: opening the modal again
 *   refetches a fresh empty form, guaranteeing zero stale state.
 */
function initComposeModal({ sendErrorText } = {}) {
  const modal = document.getElementById('composeModal');
  if (!modal || modal._composeBound) return;
  modal._composeBound = true;

  const url = modal.getAttribute('data-compose-url');
  if (!url) return;

  const composeModal = createDynamicModal({
    modalId: 'composeModal',
    onContentInjected: async () => {
      if (typeof window.initWysiwyg !== 'function') {
        await import(/* webpackChunkName: "wysiwyg" */ '../utils/ui/wysiwyg.js');
      }
      if (typeof window.initWysiwyg === 'function') window.initWysiwyg();
      if (typeof window.reinitFlowbiteWidgets === 'function') window.reinitFlowbiteWidgets();
    },
    beforeSubmit: (form) => {
      const editorEl = document.getElementById('wysiwyg-example');
      const hiddenBody = form.querySelector('#compose-body-hidden');
      if (editorEl && editorEl._tiptapEditor && hiddenBody) {
        hiddenBody.value = editorEl._tiptapEditor.getHTML();
      }
    },
    onSuccess: (_data, helper) => {
      // afterClose so the soft-reload fires once the leave transition
      // is fully done — otherwise the Pattern B partial swap destroys
      // the backdrop mid-fade and the user sees a "white flash".
      helper.close(() => {
        if (typeof window._softReload === 'function') window._softReload();
      });
    },
    errorLoadingMessage: sendErrorText || 'Could not load compose form. Please try again.',
  });

  if (!composeModal) return;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-compose-open]');
    if (!trigger) return;
    event.preventDefault();
    const title = trigger.getAttribute('data-compose-title') || 'New message';
    composeModal.open(url, title);
  });
}


// ═══════════════════════════════════════════════════════
//  Inline Reply / Reply-All / Forward (split-pane)
// ═══════════════════════════════════════════════════════

function initInlineReplyHandlers(container, opts) {
  const links = container.querySelectorAll('.mail-reply-link[data-reply-partial-url]');
  if (links.length === 0) return;

  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const partialUrl = link.getAttribute('data-reply-partial-url');
      loadReplyForm(container, partialUrl, opts);
    });
  });
}

async function loadReplyForm(container, partialUrl, opts) {
  container.innerHTML = '<div class="flex items-center justify-center flex-1 p-8"><svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>';

  try {
    const resp = await fetch(partialUrl);
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const html = await resp.text();
    container.innerHTML = html;

    renderEmailBodyFrame(container);

    // Init TipTap WYSIWYG editor
    if (window.initWysiwyg) {
      window.initWysiwyg();
    }

    if (window.reinitFlowbiteWidgets) window.reinitFlowbiteWidgets();

    // Init recipient badge management
    initReplyForm({
      recipientRequiredText: opts.recipientRequiredText,
      invalidEmailText: opts.invalidEmailText,
      errorTitle: opts.errorTitle,
    });

    // Toggle quoted email visibility (visible by default, click to collapse)
    const toggleBtn = container.querySelector('#toggle-quoted-email');
    const quotedBody = container.querySelector('#quoted-email-body');
    const toggleIcon = container.querySelector('#toggle-quoted-icon');
    if (toggleBtn && quotedBody) {
      toggleBtn.addEventListener('click', () => {
        const isNowHidden = quotedBody.classList.toggle('hidden');
        if (toggleIcon) {
          toggleIcon.style.transform = isNowHidden ? '' : 'rotate(90deg)';
        }
      });
    }

    // Discard button — reload original preview
    const discardBtn = container.querySelector('#reply-discard-btn');
    if (discardBtn && opts.reloadPreview) {
      discardBtn.addEventListener('click', () => {
        opts.reloadPreview();
      });
    }

    // AJAX form submission
    const form = container.querySelector('#replyForm');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Sync editor content to hidden input
        const editorEl = document.getElementById('wysiwyg-example');
        const hiddenBody = document.getElementById('compose-body-hidden');
        if (editorEl && editorEl._tiptapEditor && hiddenBody) {
          hiddenBody.value = editorEl._tiptapEditor.getHTML();
        }

        const formData = new FormData(form);
        const sendBtn = form.querySelector('button[type="submit"]');
        if (sendBtn) lockButton(sendBtn);

        try {
          const resp = await fetch(form.action, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRFToken': opts.csrfToken,
            },
            body: formData,
          });
          const data = await resp.json();
          if (data.ok) {
            if (typeof window.showToast === 'function') {
              window.showToast(opts.replySentText, 'success');
            }
            setTimeout(() => {
              if (opts.reloadPreview) opts.reloadPreview();
            }, 1200);
          } else {
            if (typeof window.showToast === 'function') {
              window.showToast(data.error || opts.sendErrorText, 'error');
            }
          }
        } catch (err) {
          if (typeof window.showToast === 'function') {
            window.showToast(opts.sendErrorText, 'error');
          }
        } finally {
          if (sendBtn) unlockButton(sendBtn);
        }
      });
    }
  } catch (err) {
    container.innerHTML = '<div class="flex items-center justify-center flex-1 p-8 text-sm text-gray-500 dark:text-gray-400">Failed to load reply form.</div>';
  }
}


// ═══════════════════════════════════════════════════════
//  Split-View Preview Pane
// ═══════════════════════════════════════════════════════

function initSplitViewPreview({ folder, csrfToken, readUrlTemplate, previewUrlTemplate, toggleStarUrlTemplate, recipientRequiredText, invalidEmailText, errorTitle, replySentText, sendErrorText }) {
  const previewPane = document.getElementById('email-preview-pane');
  const previewEmpty = document.getElementById('email-preview-empty');
  const previewLoading = document.getElementById('email-preview-loading');
  const previewContent = document.getElementById('email-preview-content');
  const rows = document.querySelectorAll('.mail-draggable-row');

  if (!previewPane || !previewContent || rows.length === 0) return;

  let activeMessageId = null;
  let abortController = null;

  function isLargeScreen() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function showState(state) {
    previewEmpty.className = state === 'empty'
      ? 'flex flex-col items-center justify-center flex-1 text-gray-400 dark:text-gray-500'
      : 'hidden';
    previewLoading.className = state === 'loading'
      ? 'flex flex-col items-center justify-center flex-1'
      : 'hidden';
    previewContent.className = state === 'content'
      ? 'flex flex-col flex-1 overflow-y-auto'
      : 'hidden';
  }

  function setActiveRow(messageId) {
    rows.forEach((r) => {
      if (r.getAttribute('data-message-id') === messageId) {
        r.classList.add('mail-row-active');
      } else {
        r.classList.remove('mail-row-active');
      }
    });
  }

  function markRowAsRead(messageId) {
    const row = document.querySelector(`.mail-draggable-row[data-message-id="${messageId}"]`);
    if (!row) return;
    const senderEl = row.querySelector('.text-sm.truncate');
    const subjectEl = row.querySelectorAll('.text-sm.truncate')[1];
    if (senderEl) {
      senderEl.classList.remove('font-semibold', 'text-gray-900', 'dark:text-white');
      senderEl.classList.add('text-gray-700', 'dark:text-gray-300');
    }
    if (subjectEl) {
      subjectEl.classList.remove('font-medium', 'text-gray-900', 'dark:text-white');
      subjectEl.classList.add('text-gray-600', 'dark:text-gray-400');
    }
  }

  function initPreviewIframe(container) {
    renderEmailBodyFrame(container);
  }

  async function loadPreview(messageId) {
    if (activeMessageId === messageId) return;

    if (abortController) abortController.abort();
    abortController = new AbortController();

    activeMessageId = messageId;
    setActiveRow(messageId);
    markRowAsRead(messageId);
    showState('loading');

    // Update URL query param so F5 re-selects this message
    const params = new URLSearchParams(window.location.search);
    params.set('selected', messageId);
    history.replaceState(null, '', window.location.pathname + '?' + params.toString());

    const url = previewUrlTemplate.replace('__MSG_ID__', messageId);

    try {
      const resp = await fetch(url, { signal: abortController.signal });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const html = await resp.text();
      previewContent.innerHTML = html;
      showState('content');

      initPreviewIframe(previewContent);

      if (window.reinitFlowbiteWidgets) window.reinitFlowbiteWidgets(previewContent);

      if (toggleStarUrlTemplate) {
        initStarToggle({ csrfToken, toggleStarUrlTemplate });
      }

      // Bind inline reply/forward handlers
      initInlineReplyHandlers(previewContent, {
        csrfToken,
        messageId: messageId,
        previewUrlTemplate,
        toggleStarUrlTemplate,
        recipientRequiredText,
        invalidEmailText,
        errorTitle,
        replySentText,
        sendErrorText,
        reloadPreview: () => {
          activeMessageId = null;
          loadPreview(messageId);
        },
      });

    } catch (err) {
      if (err.name === 'AbortError') return;
      previewContent.innerHTML = '<div class="flex items-center justify-center flex-1 p-8 text-sm text-gray-500 dark:text-gray-400">Failed to load message.</div>';
      showState('content');
    }
  }

  // Row click handler
  rows.forEach((row) => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('input[type="checkbox"]') || e.target.closest('.mail-star-btn')) return;

      const messageId = row.getAttribute('data-message-id');

      if (isLargeScreen()) {
        e.preventDefault();
        loadPreview(messageId);
      } else {
        const readUrl = row.getAttribute('data-read-url');
        if (readUrl) location.href = readUrl;
      }
    });
  });

  // Keyboard navigation (up/down arrows)
  document.addEventListener('keydown', (e) => {
    if (!isLargeScreen()) return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'j' || e.key === 'k') {
      e.preventDefault();
      const rowArray = Array.from(rows);
      const currentIdx = rowArray.findIndex((r) => r.getAttribute('data-message-id') === activeMessageId);
      let nextIdx;

      if (e.key === 'ArrowDown' || e.key === 'j') {
        nextIdx = currentIdx < rowArray.length - 1 ? currentIdx + 1 : currentIdx;
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : 0;
      }

      const nextRow = rowArray[nextIdx];
      if (nextRow) {
        nextRow.scrollIntoView({ block: 'nearest' });
        loadPreview(nextRow.getAttribute('data-message-id'));
      }
    }
  });

  // Auto-load previously selected message on page refresh
  const selectedParams = new URLSearchParams(window.location.search);
  const selectedId = selectedParams.get('selected');
  if (selectedId) {
    const selectedRow = document.querySelector('.mail-draggable-row[data-message-id="' + selectedId + '"]');
    if (selectedRow) {
      selectedRow.scrollIntoView({ block: 'nearest' });
      loadPreview(selectedId);
    }
  }
}


// ═══════════════════════════════════════════════════════
//  Infinite Scroll
// ═══════════════════════════════════════════════════════

function initInfiniteScroll({ folder, csrfToken, loadMoreUrl, toggleStarUrlTemplate, readUrlTemplate, previewUrlTemplate, q, recipientRequiredText, invalidEmailText, errorTitle, replySentText, sendErrorText }) {
  const listContainer = document.getElementById('email-message-list');
  if (!listContainer) return;

  let loading = false;

  const observer = new IntersectionObserver((entries) => {
    const sentinel = entries[0];
    if (!sentinel.isIntersecting || loading) return;

    const el = sentinel.target;
    const nextToken = el.getAttribute('data-next-token');
    if (!nextToken) {
      observer.unobserve(el);
      return;
    }

    loading = true;

    let url = loadMoreUrl + '?folder=' + encodeURIComponent(folder) + '&page_token=' + encodeURIComponent(nextToken);
    if (q) url += '&q=' + encodeURIComponent(q);

    fetch(url)
      .then((resp) => {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then((data) => {
        // Remove the sentinel
        observer.unobserve(el);
        el.remove();

        // Append the new rows
        if (data.html) {
          listContainer.insertAdjacentHTML('beforeend', data.html);

          // Re-init star toggles and split-view click handlers on new rows
          if (toggleStarUrlTemplate) {
            initStarToggle({ csrfToken, toggleStarUrlTemplate });
          }
          _rebindNewRows(listContainer, { readUrlTemplate, previewUrlTemplate, toggleStarUrlTemplate, csrfToken, recipientRequiredText, invalidEmailText, errorTitle, replySentText, sendErrorText });

          // Update select-all checkbox logic for new checkboxes
          _rebindSelectAll();
        }

        // If there's more data, add a new sentinel
        if (data.next_page_token) {
          const newSentinel = document.createElement('div');
          newSentinel.id = 'email-load-more-sentinel';
          newSentinel.className = 'flex items-center justify-center py-4';
          newSentinel.setAttribute('data-next-token', data.next_page_token);
          newSentinel.innerHTML = '<svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';
          listContainer.appendChild(newSentinel);
          observer.observe(newSentinel);
        }

        loading = false;
      })
      .catch(() => {
        // On error, remove sentinel spinner and show retry
        el.innerHTML = '<button class="text-sm text-primary-600 hover:underline py-2">Load more</button>';
        el.querySelector('button').addEventListener('click', () => {
          el.innerHTML = '<svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';
          loading = false;
          observer.observe(el);
        });
        loading = false;
      });
  }, {
    root: listContainer,
    rootMargin: '200px',
  });

  const sentinel = document.getElementById('email-load-more-sentinel');
  if (sentinel) observer.observe(sentinel);
}

function _rebindNewRows(listContainer, { readUrlTemplate, previewUrlTemplate, toggleStarUrlTemplate, csrfToken, recipientRequiredText, invalidEmailText, errorTitle, replySentText, sendErrorText }) {
  const previewPane = document.getElementById('email-preview-pane');
  if (!previewPane) return;

  const allRows = listContainer.querySelectorAll('.mail-draggable-row');
  const isLg = window.matchMedia('(min-width: 1024px)').matches;

  allRows.forEach((row) => {
    if (row._emailBound) return;
    row._emailBound = true;

    row.addEventListener('click', (e) => {
      if (e.target.closest('input[type="checkbox"]') || e.target.closest('.mail-star-btn')) return;
      const messageId = row.getAttribute('data-message-id');

      if (isLg && previewUrlTemplate) {
        e.preventDefault();
        const previewContent = document.getElementById('email-preview-content');
        const previewEmpty = document.getElementById('email-preview-empty');
        const previewLoading = document.getElementById('email-preview-loading');

        if (previewEmpty) previewEmpty.className = 'hidden';
        if (previewLoading) previewLoading.className = 'flex flex-col items-center justify-center flex-1';
        if (previewContent) previewContent.className = 'hidden';

        allRows.forEach((r) => {
          r.classList.remove('mail-row-active');
        });
        row.classList.add('mail-row-active');

        const url = previewUrlTemplate.replace('__MSG_ID__', messageId);
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            if (previewContent) {
              previewContent.innerHTML = html;
              previewContent.className = 'flex flex-col flex-1 overflow-y-auto';
              renderEmailBodyFrame(previewContent);
            }
            if (previewLoading) previewLoading.className = 'hidden';
            if (window.reinitFlowbiteWidgets) window.reinitFlowbiteWidgets(previewContent);
            if (toggleStarUrlTemplate) initStarToggle({ csrfToken, toggleStarUrlTemplate });
            // Bind inline reply handlers for infinite-scroll loaded rows
            if (previewContent) {
              initInlineReplyHandlers(previewContent, {
                csrfToken,
                messageId,
                previewUrlTemplate,
                toggleStarUrlTemplate,
                recipientRequiredText,
                invalidEmailText,
                errorTitle,
                replySentText,
                sendErrorText,
                reloadPreview: () => {
                  fetch(url).then((r) => r.text()).then((h) => {
                    previewContent.innerHTML = h;
                    previewContent.className = 'flex flex-col flex-1 overflow-y-auto';
                    renderEmailBodyFrame(previewContent);
                    if (window.reinitFlowbiteWidgets) window.reinitFlowbiteWidgets(previewContent);
                    if (toggleStarUrlTemplate) initStarToggle({ csrfToken, toggleStarUrlTemplate });
                    initInlineReplyHandlers(previewContent, { csrfToken, messageId, previewUrlTemplate, toggleStarUrlTemplate, recipientRequiredText, invalidEmailText, errorTitle, replySentText, sendErrorText, reloadPreview: null });
                  }).catch(() => {});
                },
              });
            }
          })
          .catch(() => {
            if (previewContent) {
              previewContent.innerHTML = '<div class="flex items-center justify-center flex-1 p-8 text-sm text-gray-500">Failed to load message.</div>';
              previewContent.className = 'flex flex-col flex-1 overflow-y-auto';
            }
            if (previewLoading) previewLoading.className = 'hidden';
          });
      } else {
        const readUrl = row.getAttribute('data-read-url');
        if (readUrl) location.href = readUrl;
      }
    });
  });
}

function _rebindSelectAll() {
  const checkAll = document.getElementById('checkbox-all');
  const rowCheckboxes = document.querySelectorAll('.mail-row-checkbox');
  if (!checkAll) return;

  checkAll.onchange = () => {
    rowCheckboxes.forEach((cb) => { cb.checked = checkAll.checked; });
  };

  rowCheckboxes.forEach((cb) => {
    if (cb._selectBound) return;
    cb._selectBound = true;
    cb.addEventListener('change', () => {
      const checkedCount = document.querySelectorAll('.mail-row-checkbox:checked').length;
      const total = document.querySelectorAll('.mail-row-checkbox').length;
      checkAll.checked = checkedCount === total;
      checkAll.indeterminate = checkedCount > 0 && checkedCount < total;
    });
  });
}


// ═══════════════════════════════════════════════════════
//  Drag-and-Drop: email rows → sidebar folder targets
// ═══════════════════════════════════════════════════════

function initDragAndDrop({ folder, csrfToken, bulkTrashUrl, bulkArchiveUrl, bulkMoveToInboxUrl }) {
  const rows = document.querySelectorAll('.mail-draggable-row');
  const dropTargets = document.querySelectorAll('[data-drop-folder]');
  if (rows.length === 0 || dropTargets.length === 0) return;

  // Map target folder → action URL (skip current folder, can't drop on self)
  const folderActionMap = {
    inbox: bulkMoveToInboxUrl,
    trash: bulkTrashUrl,
    archive: bulkArchiveUrl,
  };

  let dragGhost = null;

  // ── Helper: collect message IDs for the drag ──
  function getDragMessageIds(draggedRow) {
    const draggedId = draggedRow.getAttribute('data-message-id');
    const checked = document.querySelectorAll('.mail-row-checkbox:checked');
    const checkedIds = Array.from(checked).map((cb) => cb.getAttribute('data-message-id'));

    // If the dragged row is among checked items, move all checked
    if (checkedIds.includes(draggedId)) {
      return checkedIds;
    }
    // Otherwise move only the dragged row
    return [draggedId];
  }

  // ── Pre-create the ghost badge (must be in DOM and rendered before dragstart) ──
  dragGhost = document.createElement('div');
  dragGhost.className = 'mail-drag-ghost';
  dragGhost.innerHTML = `<svg class="mail-drag-ghost-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span>1 message</span>`;
  document.body.appendChild(dragGhost);

  const ghostLabel = dragGhost.querySelector('span');

  // ── Drag start on email rows ──
  rows.forEach((row) => {
    row.addEventListener('dragstart', (e) => {
      const ids = getDragMessageIds(row);

      // Update ghost text with count
      const count = ids.length;
      ghostLabel.textContent = `${count} ${count === 1 ? 'message' : 'messages'}`;

      // Attach message IDs to the drag event
      e.dataTransfer.setData('text/plain', JSON.stringify(ids));
      e.dataTransfer.effectAllowed = 'move';

      // Use the pre-rendered ghost as the drag image
      e.dataTransfer.setDragImage(dragGhost, 0, 0);

      // Mark ALL rows being moved as dragging (visual feedback)
      ids.forEach((id) => {
        const r = document.querySelector(`.mail-draggable-row[data-message-id="${id}"]`);
        if (r) r.classList.add('mail-row-dragging');
      });

      // Ensure sidebar email dropdown is expanded so drop targets are visible
      const emailDropdown = document.getElementById('dropdown-emails');
      if (emailDropdown && emailDropdown.classList.contains('hidden')) {
        emailDropdown.classList.remove('hidden');
      }
    });

    row.addEventListener('dragend', () => {
      // Remove dragging state from ALL rows
      document.querySelectorAll('.mail-row-dragging').forEach((r) => {
        r.classList.remove('mail-row-dragging');
      });
      // Remove any lingering drop highlights
      dropTargets.forEach((t) => t.classList.remove('mail-drop-hover'));
    });
  });

  // ── Drop targets on sidebar folder links ──
  dropTargets.forEach((target) => {
    const targetFolder = target.getAttribute('data-drop-folder');

    target.addEventListener('dragover', (e) => {
      // Only allow drop on folders different from the current one
      if (targetFolder === folder) return;
      const actionUrl = folderActionMap[targetFolder];
      if (!actionUrl) return;

      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      target.classList.add('mail-drop-hover');
    });

    target.addEventListener('dragenter', (e) => {
      if (targetFolder === folder) return;
      if (!folderActionMap[targetFolder]) return;
      e.preventDefault();
      target.classList.add('mail-drop-hover');
    });

    target.addEventListener('dragleave', (e) => {
      // Only remove highlight if we actually left the element
      if (!target.contains(e.relatedTarget)) {
        target.classList.remove('mail-drop-hover');
      }
    });

    target.addEventListener('drop', (e) => {
      e.preventDefault();
      target.classList.remove('mail-drop-hover');

      if (targetFolder === folder) return;
      const actionUrl = folderActionMap[targetFolder];
      if (!actionUrl) return;

      let ids;
      try {
        ids = JSON.parse(e.dataTransfer.getData('text/plain'));
      } catch {
        return;
      }
      if (!Array.isArray(ids) || ids.length === 0) return;

      // Submit the bulk action with the folder for redirect context
      submitBulkActionForIds(actionUrl, csrfToken, ids, { folder });
    });
  });
}


// ═══════════════════════════════════════════════════════
//  Star / Flag Toggle (AJAX)
// ═══════════════════════════════════════════════════════

const FILLED_STAR_SVG = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.083 4.104c.35-.8 1.485-.8 1.834 0l1.752 4.022a1 1 0 0 0 .84.597l4.463.342c.9.069 1.255 1.2.557 1.771l-3.33 2.723a1 1 0 0 0-.337 1.016l1.029 4.119c.214.858-.71 1.552-1.474 1.106l-3.913-2.281a1 1 0 0 0-1.008 0L7.583 19.8c-.764.446-1.688-.248-1.473-1.106l1.029-4.119a1 1 0 0 0-.337-1.016l-3.33-2.723c-.699-.571-.343-1.702.556-1.771l4.463-.342a1 1 0 0 0 .84-.597l1.752-4.022Z"/></svg>`;

const OUTLINE_STAR_SVG = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path stroke="currentColor" stroke-width="2" d="M11.083 4.104c.35-.8 1.485-.8 1.834 0l1.752 4.022a1 1 0 0 0 .84.597l4.463.342c.9.069 1.255 1.2.557 1.771l-3.33 2.723a1 1 0 0 0-.337 1.016l1.029 4.119c.214.858-.71 1.552-1.474 1.106l-3.913-2.281a1 1 0 0 0-1.008 0L7.583 19.8c-.764.446-1.688-.248-1.473-1.106l1.029-4.119a1 1 0 0 0-.337-1.016l-3.33-2.723c-.699-.571-.343-1.702.556-1.771l4.463-.342a1 1 0 0 0 .84-.597l1.752-4.022Z"/></svg>`;

// ═══════════════════════════════════════════════════════
//  Reply / Forward: Recipient Badges + Validation
// ═══════════════════════════════════════════════════════

const REMOVE_SVG = '<svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 18 18 6M6 6l12 12"/></svg>';

// Styled alert modal (replaces native alert())
function showAlertModal(title, message) {
  const existing = document.getElementById('email-alert-modal');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.id = 'email-alert-modal';
  backdrop.className = 'fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50';
  backdrop.innerHTML =
    '<div class="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-gray-700">' +
      '<h3 class="mb-2 text-base font-semibold text-gray-900 dark:text-white">' + title + '</h3>' +
      '<p class="mb-5 text-sm text-gray-500 dark:text-gray-400">' + message + '</p>' +
      '<div class="flex justify-end">' +
        '<button id="email-alert-ok" type="button" class="btn-primary px-5 py-2.5">OK</button>' +
      '</div>' +
    '</div>';

  document.body.appendChild(backdrop);

  const okBtn = document.getElementById('email-alert-ok');
  function close() { backdrop.remove(); }

  okBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  document.addEventListener('keydown', function handler(e) {
    if (e.key === 'Enter' || e.key === 'Escape') { close(); document.removeEventListener('keydown', handler); }
  });
  okBtn.focus();
}

// Basic email regex (matches what Django's EmailValidator accepts)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(str) {
  // Support "Display Name <email>" format
  const match = str.match(/<([^>]+)>/);
  const addr = match ? match[1] : str;
  return EMAIL_RE.test(addr.trim());
}

/**
 * Initialise recipient badge management and form validation on the reply/forward page.
 * @param {object} options
 * @param {string} options.recipientRequiredText - Translated "Recipient required" text.
 * @param {string} options.invalidEmailText - Translated invalid email error text.
 */
export function initReplyForm({
  recipientRequiredText = 'Recipient required',
  invalidEmailText = 'Please make sure all addresses are properly formed.',
  errorTitle = 'Error',
} = {}) {
  const container = document.getElementById('reply-recipients');
  const hiddenTo = document.getElementById('reply-to-hidden');
  const addInput = document.getElementById('reply-add-recipient');
  const form = document.getElementById('replyForm');

  if (!container || !hiddenTo) return;

  function syncHiddenTo() {
    const badges = container.querySelectorAll('.reply-recipient-badge');
    const emails = [];
    badges.forEach((b) => emails.push(b.getAttribute('data-email')));
    hiddenTo.value = emails.join(', ');
  }

  function createBadge(email) {
    const valid = isValidEmail(email);
    const span = document.createElement('span');
    span.className = valid
      ? 'reply-recipient-badge inline-flex items-center gap-1 rounded-lg bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
      : 'reply-recipient-badge reply-recipient-invalid inline-flex items-center gap-1 rounded-lg bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';
    span.setAttribute('data-email', email);
    span.innerHTML =
      email +
      '<button type="button" class="reply-recipient-remove ms-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-200 dark:hover:bg-primary-800" aria-label="Remove">' +
      REMOVE_SVG +
      '</button>';
    return span;
  }

  function addRecipient(value) {
    const email = value.trim().replace(/,$/, '').trim();
    if (!email) return;
    // Prevent duplicates
    const existing = container.querySelectorAll('.reply-recipient-badge');
    for (let i = 0; i < existing.length; i++) {
      if (existing[i].getAttribute('data-email').toLowerCase() === email.toLowerCase()) return;
    }
    container.insertBefore(createBadge(email), addInput);
    syncHiddenTo();
  }

  // Remove badge on click
  container.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('.reply-recipient-remove');
    if (!removeBtn) return;
    removeBtn.closest('.reply-recipient-badge').remove();
    syncHiddenTo();
  });

  if (addInput) {
    // Add recipient on Enter, comma, or Tab
    addInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
        if (addInput.value.trim()) {
          e.preventDefault();
          addRecipient(addInput.value);
          addInput.value = '';
        } else if (e.key === 'Enter') {
          e.preventDefault();
        }
      }
      // Backspace on empty input: remove last badge
      if (e.key === 'Backspace' && !addInput.value) {
        const badges = container.querySelectorAll('.reply-recipient-badge');
        if (badges.length) {
          badges[badges.length - 1].remove();
          syncHiddenTo();
        }
      }
    });

    // Commit on blur
    addInput.addEventListener('blur', () => {
      if (addInput.value.trim()) {
        addRecipient(addInput.value);
        addInput.value = '';
      }
    });

    // Focus input when clicking container whitespace
    container.addEventListener('click', (e) => {
      if (e.target === container) addInput.focus();
    });
  }

  // Submit handler: commit any text still in the input, then let the
  // form submit natively. Per the form-validation rule, no
  // `e.preventDefault()` and no manual spinner — the server validates
  // the `to` field, and the global `data-protect-submit` on the Send
  // button handles the spinner. Recipients flagged as invalid carry
  // `.reply-recipient-invalid` (live feedback from `createBadge`),
  // and `hiddenTo` carries the joined value to the server.
  if (form) {
    form.addEventListener('submit', () => {
      if (addInput && addInput.value.trim()) {
        addRecipient(addInput.value);
        addInput.value = '';
      }
    });
  }
}


function _setStarState(messageId, starred) {
  const btns = document.querySelectorAll('.mail-star-btn[data-message-id="' + messageId + '"]');
  btns.forEach((b) => {
    b.setAttribute('data-starred', String(starred));
    if (starred) {
      b.classList.remove('text-gray-400', 'dark:text-gray-500');
      b.classList.add('text-yellow-400');
      b.innerHTML = FILLED_STAR_SVG;
    } else {
      b.classList.remove('text-yellow-400');
      b.classList.add('text-gray-400', 'dark:text-gray-500');
      b.innerHTML = OUTLINE_STAR_SVG;
    }
  });
}

export function initStarToggle({ csrfToken, toggleStarUrlTemplate }) {
  const buttons = document.querySelectorAll('.mail-star-btn');
  if (buttons.length === 0) return;

  buttons.forEach((btn) => {
    if (btn._starBound) return;
    btn._starBound = true;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const messageId = btn.getAttribute('data-message-id');
      const isCurrentlyStarred = btn.getAttribute('data-starred') === 'true';
      const newStarred = !isCurrentlyStarred;

      // Optimistic UI update — sync ALL star buttons for this message
      const allBtns = document.querySelectorAll('.mail-star-btn[data-message-id="' + messageId + '"]');
      allBtns.forEach((b) => {
        b.setAttribute('data-starred', String(newStarred));
        if (newStarred) {
          b.classList.remove('text-gray-400', 'dark:text-gray-500');
          b.classList.add('text-yellow-400');
          b.innerHTML = FILLED_STAR_SVG;
        } else {
          b.classList.remove('text-yellow-400');
          b.classList.add('text-gray-400', 'dark:text-gray-500');
          b.innerHTML = OUTLINE_STAR_SVG;
        }
      });

      // POST to backend
      const url = toggleStarUrlTemplate.replace('__MSG_ID__', messageId);
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({ starred: newStarred }),
        });
        if (!resp.ok) {
          // Revert on failure — sync all buttons
          _setStarState(messageId, isCurrentlyStarred);
        }
      } catch {
        // Revert on network error — sync all buttons
        _setStarState(messageId, isCurrentlyStarred);
      }
    });
  });
}


// ═══════════════════════════════════════════════════════
//  Auto-init entry points (Phase 21.B)
//
//  Each consumer template emits a tiny ``[data-auto-init="..."]``
//  wrapper element with the page's config carried as ``data-*``
//  attributes. ``app.js`` walks ``[data-auto-init]`` and calls the
//  matching ``autoInit<Name>`` exported below.
//
//  Why ``data-*`` and not ``json_script``: the config is mostly
//  short URLs and translated strings — ``data-*`` keeps it readable
//  in DevTools, requires no view-side dict, and Django's automatic
//  HTML attribute escaping handles the special chars correctly.
// ═══════════════════════════════════════════════════════

/**
 * Auto-init for the inbox/sent/trash/archive list page.
 * Reads config from a ``[data-auto-init="emailList"]`` wrapper.
 */
export function autoInitEmailList() {
  const el = document.querySelector('[data-auto-init="emailList"]');
  if (!el) return;
  const ds = el.dataset;
  initEmailList({
    folder: ds.folder || 'inbox',
    csrfToken: ds.csrfToken || '',
    bulkTrashUrl: ds.bulkTrashUrl || '',
    bulkArchiveUrl: ds.bulkArchiveUrl || '',
    bulkMarkUnreadUrl: ds.bulkMarkUnreadUrl || '',
    bulkMoveToInboxUrl: ds.bulkMoveToInboxUrl || '',
    toggleStarUrlTemplate: ds.toggleStarUrlTemplate || '',
    readUrlTemplate: ds.readUrlTemplate || '',
    previewUrlTemplate: ds.previewUrlTemplate || '',
    loadMoreUrl: ds.loadMoreUrl || '',
    q: ds.q || '',
    recipientRequiredText: ds.recipientRequiredText,
    invalidEmailText: ds.invalidEmailText,
    errorTitle: ds.errorTitle,
    replySentText: ds.replySentText,
    sendErrorText: ds.sendErrorText,
    printFromText: ds.printFromText,
    printToText: ds.printToText,
    printDateText: ds.printDateText,
  });
}

/**
 * Init for the full-page Read view (``office/emails/read.html``):
 * star toggle + sandbox iframe rendering for the quoted body.
 *
 * Print support comes "for free" — ``_message_preview.html`` carries
 * the ``data-print-email`` declarative trigger; ``setEmailPrintI18n``
 * here ensures the localized "From / To / Date" labels are loaded
 * before the user clicks print.
 */
export function initEmailRead(options = {}) {
  const {
    csrfToken = '',
    toggleStarUrlTemplate = '',
    printFromText,
    printToText,
    printDateText,
  } = options;
  setEmailPrintI18n({ fromText: printFromText, toText: printToText, dateText: printDateText });
  if (toggleStarUrlTemplate) {
    initStarToggle({ csrfToken, toggleStarUrlTemplate });
  }
  renderEmailBodyFrame(document);
}

export function autoInitEmailRead() {
  const el = document.querySelector('[data-auto-init="emailRead"]');
  if (!el) return;
  const ds = el.dataset;
  initEmailRead({
    csrfToken: ds.csrfToken || '',
    toggleStarUrlTemplate: ds.toggleStarUrlTemplate || '',
    printFromText: ds.printFromText,
    printToText: ds.printToText,
    printDateText: ds.printDateText,
  });
}

/**
 * Init for the Reply / Reply-all / Forward full page
 * (``office/emails/reply.html``): star toggle + sandbox iframe
 * rendering for the quoted body + recipient-badge form management.
 */
export function initEmailReply(options = {}) {
  const {
    csrfToken = '',
    toggleStarUrlTemplate = '',
    recipientRequiredText = 'Recipient required',
    invalidEmailText = 'Please make sure all addresses are properly formed.',
    errorTitle = 'Error',
    printFromText,
    printToText,
    printDateText,
  } = options;
  setEmailPrintI18n({ fromText: printFromText, toText: printToText, dateText: printDateText });
  if (toggleStarUrlTemplate) {
    initStarToggle({ csrfToken, toggleStarUrlTemplate });
  }
  renderEmailBodyFrame(document);
  initReplyForm({ recipientRequiredText, invalidEmailText, errorTitle });
}

export function autoInitEmailReply() {
  const el = document.querySelector('[data-auto-init="emailReply"]');
  if (!el) return;
  const ds = el.dataset;
  initEmailReply({
    csrfToken: ds.csrfToken || '',
    toggleStarUrlTemplate: ds.toggleStarUrlTemplate || '',
    recipientRequiredText: ds.recipientRequiredText,
    invalidEmailText: ds.invalidEmailText,
    errorTitle: ds.errorTitle,
    printFromText: ds.printFromText,
    printToText: ds.printToText,
    printDateText: ds.printDateText,
  });
}
